import type { TrackMomentsAnalyticsEventRequest } from '@/store/api/momentsApi';

export const MEMBER_MOMENTS_EVENTS = {
  answeredArtifactSurvey: 'Answered artifact survey',
  clickedHomeReflection: 'Clicked home reflection',
  clickedHomeSessionPrep: 'Clicked home session prep',
  clickedMomentsIntroBannerCta: 'Clicked moments intro banner CTA',
  clickedSessionAsset: 'Clicked session asset',
  copiedArtifact: 'Copied artifact',
  copiedTranscript: 'Copied transcript',
  createdArtifactNote: 'Created artifact note',
  deletedArtifactNote: 'Deleted artifact note',
  dismissedMomentsIntroBanner: 'Dismissed moments intro banner',
  failedToLoadArtifact: 'Failed to load artifact',
  searchedHighlights: 'Searched highlights',
  sortedHighlights: 'Sorted highlights',
  submittedArtifactFeedback: 'Submitted artifact feedback',
  viewedArtifact: 'Viewed artifact',
  viewedHighlights: 'Viewed highlights',
  viewedMomentsIntroBanner: 'Viewed moments intro banner',
  viewedTranscript: 'Viewed transcript',
} as const;

export type MemberMomentsAnalyticsEventName =
  (typeof MEMBER_MOMENTS_EVENTS)[keyof typeof MEMBER_MOMENTS_EVENTS];

// Keep accepting arbitrary event names while giving member-side events a typed vocabulary.
export type MomentsAnalyticsEventName =
  MemberMomentsAnalyticsEventName | (string & {});

export type MemberMomentsAnalyticsSurface =
  | 'home'
  | 'session_detail'
  | 'artifact_detail'
  | 'transcript_detail'
  | 'highlights';

export interface MemberMomentsAnalyticsProperties {
  artifactId?: string;
  assetKind?: 'reflection' | 'prep' | 'transcript' | 'summary';
  copyKind?: 'link' | 'markdown' | 'transcript';
  destination?: string;
  entrypoint?: string;
  feedbackType?: 'positive' | 'negative';
  highlightCount?: number;
  position?: number;
  profileSlug?: string;
  queryLength?: number;
  route: string;
  sessionId?: string;
  sort?: string;
  surface: MemberMomentsAnalyticsSurface;
  // Free-text content stays out of Mixpanel; "<submitted>" marks a saved answer.
  surveyAnswer?: 'yes' | 'not_really' | 'no' | '<submitted>';
  surveyId?: string;
  surveyQuestion?: 'got_you' | 'will_try' | 'stood_out';
  surveyResponseId?: string;
}

export const buildMemberMomentsAnalyticsProperties = ({
  artifactId,
  assetKind,
  copyKind,
  destination,
  entrypoint,
  feedbackType,
  highlightCount,
  position,
  profileSlug,
  queryLength,
  route,
  sessionId,
  sort,
  surface,
  surveyAnswer,
  surveyId,
  surveyQuestion,
  surveyResponseId,
}: MemberMomentsAnalyticsProperties): Record<string, unknown> => ({
  artifact_id: artifactId,
  asset_kind: assetKind,
  copy_kind: copyKind,
  destination,
  entrypoint,
  feedback_type: feedbackType,
  highlight_count: highlightCount,
  position,
  profile_slug: profileSlug,
  query_length: queryLength,
  route,
  session_id: sessionId,
  sort,
  surface,
  survey_answer: surveyAnswer,
  survey_id: surveyId,
  survey_question: surveyQuestion,
  survey_response_id: surveyResponseId,
});

export interface MomentsAnalyticsTrackOptions {
  clientEventId?: string;
  dedupeKey?: string;
  queryParams?: Record<string, string>;
}

interface DispatchResult {
  unwrap: () => Promise<unknown>;
}

type DispatchAnalyticsEvent = (
  request: TrackMomentsAnalyticsEventRequest,
) => DispatchResult;

let configuredDispatchAnalyticsEvent: DispatchAnalyticsEvent | null = null;

export const configureMomentsAnalytics = (
  dispatchAnalyticsEvent: DispatchAnalyticsEvent,
): void => {
  configuredDispatchAnalyticsEvent = dispatchAnalyticsEvent;
};

interface CreateMomentsAnalyticsArgs {
  dispatchAnalyticsEvent: DispatchAnalyticsEvent;
  isSuppressed?: () => boolean;
  isDev?: () => boolean;
  logDebug?: (message: string, error?: unknown) => void;
  now?: () => Date;
  randomUUID?: () => string;
}

const defaultRandomUUID = () => crypto.randomUUID();

const formatAnalyticsError = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const createMomentsAnalytics = ({
  dispatchAnalyticsEvent,
  isDev = () => import.meta.env.DEV,
  isSuppressed = () => false,
  logDebug = (message, error) => {
    if (error === undefined) {
      console.error(message);
    } else {
      console.error(message, error);
    }
  },
  now = () => new Date(),
  randomUUID = defaultRandomUUID,
}: CreateMomentsAnalyticsArgs) => {
  const pageLoadId = randomUUID();
  const dedupeClientEventIds = new Map<string, string>();

  const resolveClientEventId = ({
    clientEventId,
    dedupeKey,
  }: MomentsAnalyticsTrackOptions): string => {
    if (clientEventId) {
      return clientEventId;
    }

    if (dedupeKey) {
      const scopedKey = `${pageLoadId}:${dedupeKey}`;
      const existing = dedupeClientEventIds.get(scopedKey);
      if (existing) {
        return existing;
      }

      const generated = randomUUID();
      dedupeClientEventIds.set(scopedKey, generated);
      return generated;
    }

    return randomUUID();
  };

  const track = (
    eventName: MomentsAnalyticsEventName,
    properties: Record<string, unknown> = {},
    options: MomentsAnalyticsTrackOptions = {},
  ): void => {
    if (isSuppressed()) {
      if (isDev()) {
        logDebug(`[moments-analytics] ${eventName} suppressed`);
      }
      return;
    }

    const clientEventId = resolveClientEventId(options);
    const occurredAt = now().toISOString();

    try {
      const result = dispatchAnalyticsEvent({
        clientEventId,
        eventName,
        occurredAt,
        properties,
        queryParams: options.queryParams,
      });

      result.unwrap().catch((error) => {
        if (isDev()) {
          logDebug(
            `[moments-analytics] ${eventName} failed: ${formatAnalyticsError(error)}`,
            error,
          );
        }
      });
    } catch (error) {
      if (isDev()) {
        logDebug(
          `[moments-analytics] ${eventName} failed: ${formatAnalyticsError(error)}`,
          error,
        );
      }
    }
  };

  const viewed = (
    eventName: MomentsAnalyticsEventName,
    screen: string,
    route: string,
    properties: Record<string, unknown> = {},
    options: MomentsAnalyticsTrackOptions = {},
  ): void => {
    // MEN-5426: screen views carry the page URL's query parameters, e.g. email
    // UTMs.
    track(
      eventName,
      { route, screen, ...properties },
      {
        ...options,
        queryParams: Object.fromEntries(
          new URLSearchParams(window.location.search),
        ),
      },
    );
  };

  return { track, viewed };
};

// The app configures this before rendering. Keeping the store out of this module
// lets tracking remain safe to import from isolated component tests.
export const momentsAnalytics = createMomentsAnalytics({
  dispatchAnalyticsEvent: (request) =>
    configuredDispatchAnalyticsEvent?.(request) ?? {
      unwrap: () => Promise.resolve({ queued: false }),
    },
});
